import type { UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from '@/react-query/api';

import { endpoints } from '../endpoints';

const REFETCH_INTERVAL_IN_SECONDS = 30;

export interface GetCombinedAppointmentsResponse {
  nextCursor: string | null;
  appointments: (
    | VoiceNotesSearchUnifiedAppointment
    | VoiceNotesSearchStandaloneVisit
  )[];
}

export interface VoiceNotesSearchUnifiedAppointment {
  id: string;
  appointmentOn: string; // ISO string
  pmsPatients: VoiceNotesSearchPmsPatient[];
  imagingPatients: VoiceNotesSearchImagingPatient[];
}

export interface VoiceNotesSearchStandaloneVisit {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  appointmentOn: string;
  sourcePatientId: string | null;
}

export interface VoiceNotesSearchPmsPatient {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}

export interface VoiceNotesSearchImagingPatient {
  id: string;
  name: string;
  dateOfBirth: string;
}

export interface CombinedAppointmentSearchRequest {
  // optional filters for the appointment
  appointmentOpts?: {
    startDate?: string; // ISO 8601 string
    endDate?: string; // ISO 8601 string
  };

  // optional filters for patient name
  patientOpts?: {
    name?: string;
  };

  meta?: {
    // optional pagination and cursor info
    pagination?: {
      cursor?: string;
      limit?: number;
    };

    // optional sort field and direction
    sort?: {
      [field: string]: 'ASC' | 'DESC';
    };
  };
}

export const useCombinedAppointments = (
  args: CombinedAppointmentSearchRequest,
  options?: UseInfiniteQueryOptions<GetCombinedAppointmentsResponse, unknown>,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  const practiceId = currentPractice;
  const orgId = currentOrg;

  return useInfiniteQuery({
    queryKey: [
      endpoints.ambient.voiceNotes.combinedAppointmentsList,
      args,
      practiceId,
    ],
    queryFn: ({ pageParam }) =>
      makeAPIRequestReactQuery<GetCombinedAppointmentsResponse>(
        endpoints.ambient.voiceNotes.combinedAppointmentsList,
        {
          ...args,
          meta: {
            ...args?.meta,
            pagination: {
              cursor: pageParam,
              limit: args?.meta?.pagination?.limit,
            },
          },
        },
        'get',
        { headers: { practice: practiceId, org: orgId } },
      ),
    getNextPageParam: (lastPage) => lastPage?.nextCursor,
    refetchInterval: REFETCH_INTERVAL_IN_SECONDS * 1000,
    enabled: Boolean(practiceId) && Boolean(orgId),
    ...options,
  });
};
